/**
 * Permission Engine - Input Validation
 *
 * Validates user context and permission requirements
 */

import { systemLogger } from '@/lib/logging';
import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  UserRole,
  PERMISSION_CONFIG
} from '../types';

/**
 * Validate permission evaluation inputs
 */
export function validateInputs(user: UserContext, requirement: PermissionRequirement): PermissionResult {
  // Additional validation for security - require essential fields for permission calculations
  if (user.id && (!user.app_role && !user.is_system_admin && !user.profile?.role)) {
    return createErrorResult('INCOMPLETE_USER_CONTEXT', 'Invalid user context');
  }

  if (!user.id) {
    return createErrorResult('INVALID_USER', 'User ID is required');
  }

  if (!requirement.action || !requirement.resource) {
    return createErrorResult('INVALID_REQUIREMENT', 'Action and resource are required');
  }

  return createSuccessResult('INPUT_VALIDATION', 'Inputs validated successfully');
}

/**
 * Get user role from context with fallback logic
 */
export function getUserRole(user: UserContext): UserRole {
  // Try primary app_role first
  if (user.app_role) {
    return user.app_role as UserRole;
  }

  // Fallback to profile role
  if (user.profile?.role) {
    return user.profile.role as UserRole;
  }

  // Default to USER role
  return UserRole.USER;
}

/**
 * Check if user is system admin
 */
export function isSystemAdmin(user: UserContext): boolean {
  return user.is_system_admin === true || user.app_role === 'ADMIN';
}

/**
 * Create success result
 */
export function createSuccessResult(_ruleName: string, reason: string): PermissionResult {
  return {
    allowed: true,
    reason
  };
}

/**
 * Create error result
 */
export function createErrorResult(code: string, reason: string): PermissionResult {
  if (PERMISSION_CONFIG.DEBUG_MODE) {
    systemLogger.warn(`Permission denied: ${code} - ${reason}`);
  }

  return {
    allowed: false,
    reason
  };
}